import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { LOCAL_STORAGE_API_KEY, goodfellasApi } from '../apis'
import { parseJwt } from '../functions/utils'

interface AuthContextType {
  token: string | null
  phone?: string
  // False until the initial session restore (/auth/session + localStorage) finishes.
  // Consumers must wait for this before treating a null token as "logged out".
  isInitialized: boolean
  login: (token: string) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

const isBrowser = typeof window !== 'undefined'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null)
  const [phone, setPhone] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    let cancelled = false
    goodfellasApi
      .get<{ phone: string }>('/auth/session')
      .then((res) => {
        if (cancelled) return
        if (res.status === 200 && res.data?.phone) {
          setToken('session')
          setPhone(res.data.phone)
        } else {
          const storedToken = isBrowser
            ? localStorage.getItem(LOCAL_STORAGE_API_KEY)
            : null
          if (storedToken) setToken(storedToken)
        }
      })
      .catch(() => {
        if (cancelled) return
        const storedToken = isBrowser
          ? localStorage.getItem(LOCAL_STORAGE_API_KEY)
          : null
        if (storedToken) setToken(storedToken)
      })
      .finally(() => {
        if (!cancelled) setIsInitialized(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!isInitialized) return

    if (!token) {
      setPhone('')
      if (isBrowser) localStorage.removeItem(LOCAL_STORAGE_API_KEY)
      return
    }

    if (token === 'session') return

    const { phone: parsedPhone } = parseJwt(token)
    setPhone(parsedPhone)
    if (isBrowser) localStorage.setItem(LOCAL_STORAGE_API_KEY, token)
  }, [token, isInitialized])

  const login = useCallback((newToken: string) => {
    const { phone: parsedPhone } = parseJwt(newToken)
    setToken(newToken)
    setPhone(parsedPhone)
  }, [])

  function clearAllCookies() {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i]
      const name = cookie.split('=')[0].trim()
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`
    }
  }

  const logout = useCallback(() => {
    if (isBrowser) {
      localStorage.removeItem(LOCAL_STORAGE_API_KEY)
      goodfellasApi
        .post('/auth/logout')
        .catch(() => undefined)
        .finally(() => {
          clearAllCookies()
          setToken(null)
          setPhone('')
          window.location.href = '/avaliacao'
        })
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{ token, login, logout, phone, isInitialized }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
